import { useEffect, useRef, useState } from "react";

import { RadioButtonChecked } from "@mui/icons-material";
import { Box, Collapse, IconButton, useMediaQuery } from "@mui/material";
import { ChatCommandHelper } from "mui-toolkit/chatPopup/ChatCommandHelper";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";

import { HeyTilleyIcon } from "../components/Icons/Icons";
import { useChatAssistantAttachments } from "../contexts/useChatAssistantAttachments";

export const ChatAssistantButton = ({
  systemPrompt,
  attachments,
  onChangeTranscript,
  onChangeRecords,
  placeholder,
}) => {
  const timerRef = useRef(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showHelper, setShowHelper] = useState(false);
  const [showButton, setShowButton] = useState(true);
  const { attachments: contextAttachments } = useChatAssistantAttachments();

  const { listening, transcript, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });

  // Use provided attachments or fall back to context attachments
  const effectiveAttachments = attachments || contextAttachments;

  useEffect(() => {
    if (!browserSupportsSpeechRecognition) {
      alert("Browser doesn't support speech recognition");
    }
  }, [browserSupportsSpeechRecognition]);

  useEffect(() => {
    if (!listening) {
      onChangeTranscript?.();
    }
  }, [transcript, listening]);

  useEffect(() => {
    if (listening) {
      timerRef.current = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setElapsedTime(0);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [listening]);

  useEffect(() => {
    if (elapsedTime >= 30 && listening) {
      handleStopRecording();
      return;
    }
  }, [elapsedTime, listening]);

  const handleStartRecording = async () => {
    resetTranscript();
    // https://www.npmjs.com/package/react-speech-recognition#basic-example
    SpeechRecognition.startListening({ continuous: true });
  };

  const handleStopRecording = async () => {
    // https://www.npmjs.com/package/react-speech-recognition#basic-example
    SpeechRecognition.stopListening();
  };

  const handleChatCompletion = async (_affectedRecords) => {
    resetTranscript();

    if (_affectedRecords) {
      // Test _account that it contains valid Array
      if (Array.isArray(_affectedRecords)) {
        onChangeRecords?.(_affectedRecords);
        // enqueueSnackbar("Account added", { preventDuplicate: true, variant: 'info' });
      }
    }
  };

  const toggleHelper = () => {
    if (showHelper) {
      // When closing, start the collapse animation but keep button hidden
      setShowHelper(false);
    } else {
      // When opening, hide button and show helper
      setShowButton(false);
      setShowHelper(true);
    }
  };

  const handleCloseHelper = () => {
    setShowHelper(false);
  };

  const handleCollapseExited = () => {
    // Show button only after collapse animation completes
    setShowButton(true);
  };

  // ---

  return (
    <Box className={!showHelper ? "chat_assistant_button" : isMobile ? "mobile_chat_assistant_button" : ""}>
      {showButton && !showHelper && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
            <IconButton
              onClick={toggleHelper}
              sx={{
                bgcolor: "primary.main",
                color: "white",
                "&:hover": {
                  bgcolor: "primary.dark",
                },
                width: "48px",
                height: "48px",
                position: "relative",
              }}
            >
              {listening ? (
                <RadioButtonChecked className="pulsating" sx={{ color: "white" }} />
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M4 9.77778V14.2222M8 6.44444V17.5556M12 2V22M16 9.77778V14.2222M20 6.44444V17.5556"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              <Box
                sx={{
                  position: "absolute",
                  right: "-4px",
                  top: "-4px",
                }}
              >
                <HeyTilleyIcon sx={{ width: "28px", height: "28px" }} color="secondary" />
              </Box>
            </IconButton>
          </Box>
        </Box>
      )}

      <Collapse in={showHelper} timeout="auto" unmountOnExit onExited={handleCollapseExited}>
        <Box
          sx={{
            // Force block display on mobile to break to new line
            display: { xs: "block", md: "inline-block" },
            width: { xs: "100%", md: "600px" },
            mt: { xs: 2, md: 0 },
          }}
        >
          <ChatCommandHelper
            promptName={systemPrompt}
            userMessage={transcript}
            onChatCompletion={handleChatCompletion}
            elapsedTime={elapsedTime}
            listening={listening}
            onStopRecording={handleStopRecording}
            onStartRecording={handleStartRecording}
            onClose={handleCloseHelper}
            placeholder={placeholder}
          />
        </Box>
      </Collapse>
    </Box>
  );
};
